import {Grid, Show, VStack} from 'platform/foundation';
import {formatNumber} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isFalsy, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {Comment} from '../../../components/Comment/CommentExpandable';
import {CommentExpandable} from '../../../components/CommentExpandable/CommentExpandable';
import {Separator} from '../../../components/Separator/Separator';
import {useLocale} from '../../../hooks/useLocale';
import i18n from '../../../i18n/i18n';
import {getYearOfManufacture} from '../../../utils/getYearOfManufacture';
import {SpecificationItem} from './SpecificationItem';

interface SpecificationContentProps extends TestIdProps {
  vin?: string | Nullish;
  yearOfManufacture?: string | Nullish;
  mileage?: number | Nullish;
  bodywork?: string | Nullish;
  differential?: string | Nullish;
  transmission?: string | Nullish;
  fuel?: string | Nullish;
  numberOfSeat?: number | Nullish;
  euroClass?: string | Nullish;
  co2Emissions?: string | Nullish;
  power?: number | Nullish;
  comment?: string | Nullish;
  isPrintView?: boolean;
  registrationPlate?: string | Nullish;
  showEmptyFields: boolean;
}

export function SpecificationContent({showEmptyFields, ...props}: SpecificationContentProps) {
  const locale = useLocale();

  const mileage = isNotNil(props?.mileage)
    ? `${formatNumber(locale.localeConfig, props.mileage, 0)} ${i18n.t('metricKm')}`
    : null;
  const power = isNotNil(props?.power) ? `${props.power} ${i18n.t('metricKw')}` : null;
  const co2Emissions = isNotNil(props.co2Emissions)
    ? `${props.co2Emissions} ${i18n.t('metricGkm')}`
    : null;

  return (
    <VStack spacing={[4, 6, 6, 8]}>
      <Grid columns={[1, 2, 2, 3]} spacing={[4, 6, 6, 10]}>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.vin)}>
          <SpecificationItem
            data-testid={suffixTestId('vin', props)}
            icon="vin"
            value={props.vin ?? EMPTY_PLACEHOLDER}
            category={i18n.t('vin')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.registrationPlate)}>
          <SpecificationItem
            data-testid={suffixTestId('registrationPlate', props)}
            icon="licensePlate"
            value={props.registrationPlate ?? EMPTY_PLACEHOLDER}
            category={i18n.t('licensePlate')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.yearOfManufacture)}>
          <SpecificationItem
            data-testid={suffixTestId('yearOfManufacture', props)}
            icon="action/date_range"
            value={getYearOfManufacture(props.yearOfManufacture) ?? EMPTY_PLACEHOLDER}
            category={i18n.t('yearOfManufacture')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(mileage)}>
          <SpecificationItem
            data-testid={suffixTestId('mileage', props)}
            icon="mileage"
            value={mileage ?? EMPTY_PLACEHOLDER}
            category={i18n.t('mileage')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.bodywork)}>
          <SpecificationItem
            data-testid={suffixTestId('bodywork', props)}
            icon="car"
            value={props.bodywork ?? EMPTY_PLACEHOLDER}
            category={i18n.t('bodywork')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.differential)}>
          <SpecificationItem
            data-testid={suffixTestId('differential', props)}
            icon="differential"
            value={props.differential ?? EMPTY_PLACEHOLDER}
            category={i18n.t('differential')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.transmission)}>
          <SpecificationItem
            data-testid={suffixTestId('transmission', props)}
            icon="transmission"
            value={props.transmission ?? EMPTY_PLACEHOLDER}
            category={i18n.t('transmission')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.fuel)}>
          <SpecificationItem
            data-testid={suffixTestId('fuel', props)}
            icon="fuelType"
            value={props.fuel ?? EMPTY_PLACEHOLDER}
            category={i18n.t('fuel')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.numberOfSeat)}>
          <SpecificationItem
            data-testid={suffixTestId('numberOfSeat', props)}
            icon="seat"
            value={props.numberOfSeat ?? EMPTY_PLACEHOLDER}
            category={i18n.t('numberOfSeat')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.euroClass)}>
          <SpecificationItem
            data-testid={suffixTestId('euroClass', props)}
            icon="euroClass"
            value={props.euroClass ?? EMPTY_PLACEHOLDER}
            category={i18n.t('euroClass')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.co2Emissions)}>
          <SpecificationItem
            data-testid={suffixTestId('co2Emissions', props)}
            icon="co2"
            value={co2Emissions ?? EMPTY_PLACEHOLDER}
            category={i18n.t('co2Emissions')}
          />
        </Show>
        <Show when={showEmptyFields || isNotNilOrEmpty(props.power)}>
          <SpecificationItem
            data-testid={suffixTestId('power', props)}
            icon="engine"
            value={power ?? EMPTY_PLACEHOLDER}
            category={i18n.t('power')}
          />
        </Show>
      </Grid>

      <Show when={isFalsy(props.isPrintView) && isNotNilOrEmpty(props.comment)}>
        <Separator />
        <CommentExpandable comment={props.comment} />
      </Show>
      <Show when={isTrue(props.isPrintView) && isNotNilOrEmpty(props.comment)}>
        <Comment comment={props.comment} />
      </Show>
    </VStack>
  );
}
